import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './context/AuthContext';

function AdminPanel() {
    const { token } = useAuth();
    
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [feedback, setFeedback] = useState({ message: '', error: '' });
    const [loadingAccesses, setLoadingAccesses] = useState(false);
    
    const [sistemas, setSistemas] = useState([]);
    const [agencias, setAgencias] = useState([]);
    const [userAccesses, setUserAccesses] = useState([]);
    const [userAgencyAccesses, setUserAgencyAccesses] = useState([]);
    const [filtroAgencia, setFiltroAgencia] = useState('');
    const [filtroSistema, setFiltroSistema] = useState('');

    const [newUser, setNewUser] = useState({ nombre: '', email: '', password: '', rol: 'usuario' });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const headers = { 'Authorization': `Bearer ${token}` };
            
            const [usersResponse, sistemasResponse, agenciasResponse] = await Promise.all([
                fetch('/api/users', { headers }),
                fetch('/api/sistemas', { headers }),
                fetch('/api/agencias', { headers })
            ]);

            if (!usersResponse.ok) throw new Error('Error al cargar usuarios.');
            if (!sistemasResponse.ok) throw new Error('Error al cargar sistemas.');
            if (!agenciasResponse.ok) throw new Error('Error al cargar agencias.');

            const usersData = await usersResponse.json();
            const sistemasData = await sistemasResponse.json();
            const agenciasData = await agenciasResponse.json();

            setUsers(usersData);
            setSistemas(sistemasData);
            setAgencias(agenciasData);

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (token) {
            fetchData();
        }
    }, [token, fetchData]);

    // ✅ PARTE 1 DE LA CORRECCIÓN: Se simplifica la función para manejar arrays simples de IDs
    const handleSelectUser = async (user) => {
        setSelectedUser(user);
        setLoadingAccesses(true);
        setFeedback({ message: '', error: '' });
        try {
            const headers = { 'Authorization': `Bearer ${token}` };

            const sistemasResponse = await fetch(`/api/users/${user.id}/accesos`, { headers });
            if (!sistemasResponse.ok) throw new Error('Error al cargar accesos de sistemas.');
            const sistemasData = await sistemasResponse.json(); // Esto es un array como [1, 2]

            const agenciasResponse = await fetch(`/api/users/${user.id}/accesos-agencias`, { headers });
            if (!agenciasResponse.ok) throw new Error('Error al cargar accesos de agencias.');
            const agenciasData = await agenciasResponse.json(); // Esto es un array como [1, 3, 33]

            // Nos aseguramos de que ambos arrays contengan solo NÚMEROS
            setUserAccesses(sistemasData.map(id => Number(id)));
            setUserAgencyAccesses(agenciasData.map(id => Number(id)));

        } catch (err) {
            setFeedback({ message: '', error: err.message });
        } finally {
            setLoadingAccesses(false);
        }
    };


    const handleNewUserChange = (e) => {
        const { name, value } = e.target;
        setNewUser(prev => ({ ...prev, [name]: value }));
    };

    const handleNewUserSubmit = async (e) => {
        e.preventDefault();
        setFeedback({ message: '', error: '' });
        try {
            const response = await fetch('/api/users', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify(newUser),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Error al crear usuario.');
            }
            setFeedback({ message: data.message, error: '' });
            fetchData();
            setNewUser({ nombre: '', email: '', password: '', rol: 'usuario' });
        } catch (err) {
            setFeedback({ message: '', error: err.message });
        }
    };

    const handleAssignSystemPermission = async (sistemaId) => {
        if (!selectedUser) return;
        setFeedback({ message: '', error: '' });
        try {
            const response = await fetch('/api/accesos', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ usuario_id: selectedUser.id, sistema_id: sistemaId }),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Error al asignar permiso de sistema.');
            }
            setUserAccesses(prev => [...prev, Number(sistemaId)]);
            setFeedback({ message: 'Permiso de sistema asignado.', error: '' });
        } catch (err) {
            setFeedback({ message: '', error: err.message });
        }
    };
    
    const handleRevokeSystemPermission = async (sistemaId) => {
        if (!selectedUser) return;
        setFeedback({ message: '', error: '' });
        try {
            const response = await fetch(`/api/accesos/${selectedUser.id}/${sistemaId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` },
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Error al revocar permiso de sistema.');
            }
            setUserAccesses(prev => prev.filter(id => id !== Number(sistemaId)));
            setFeedback({ message: 'Permiso de sistema revocado.', error: '' });
        } catch (err) {
            setFeedback({ message: '', error: err.message });
        }
    };

    const handleAssignAgencyPermission = async (agenciaId) => {
        if (!selectedUser) return;
        setFeedback({ message: '', error: '' });
        try {
            const response = await fetch('/api/accesos-agencia', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ usuario_id: selectedUser.id, agencia_id: agenciaId }),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Error al asignar permiso de agencia.');
            }
            setUserAgencyAccesses(prev => [...prev, Number(agenciaId)]);
            setFeedback({ message: 'Permiso de agencia asignado.', error: '' });
        } catch (err) {
            setFeedback({ message: '', error: err.message });
        }
    };

    const handleRevokeAgencyPermission = async (agenciaId) => {
        if (!selectedUser) return;
        setFeedback({ message: '', error: '' });
        try {
            const response = await fetch(`/api/accesos-agencia/${selectedUser.id}/${agenciaId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` },
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Error al revocar permiso de agencia.');
            }
            setUserAgencyAccesses(prev => prev.filter(id => id !== Number(agenciaId)));
            setFeedback({ message: 'Permiso de agencia revocado.', error: '' });
        } catch (err) {
            setFeedback({ message: '', error: err.message });
        }
    };

    const handleRolChange = async (rol) => {
        if (!selectedUser) return;
        try {
            const response = await fetch(`/api/users/${selectedUser.id}/rol`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ rol }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Error al actualizar el rol.');
            }

            setFeedback({ message: 'Rol de usuario actualizado.', error: '' });
            setSelectedUser(prev => ({ ...prev, rol: rol }));
            fetchData();
        } catch (err) {
            setFeedback({ message: '', error: err.message });
        }
    }

    const sistemasFiltrados = sistemas.filter(sistema =>
        sistema.nombre.toLowerCase().includes(filtroSistema.toLowerCase())
    );

    const agenciasFiltradas = agencias.filter(agencia =>
        agencia.nombre.toLowerCase().includes(filtroAgencia.toLowerCase())
    );


    if (loading) {
        return <div className="text-center mt-5"><div className="spinner-border" role="status"><span className="visually-hidden">Cargando...</span></div></div>;
    }

    if (error) {
        return <div className="alert alert-danger mt-5 container">{error}</div>;
    }

    return (
        <div className="container mt-4">
            <div className="card shadow-sm">
                <div className="card-header bg-primary text-white">
                    <h4 className="mb-0">Panel de Administración de Usuarios</h4>
                </div>
                <div className="card-body">
                    {feedback.message && <div className="alert alert-success">{feedback.message}</div>}
                    {feedback.error && <div className="alert alert-danger">{feedback.error}</div>}
                    <div className="row g-3">
                        <div className="col-md-5">
                            <div className="card h-100">
                                <div className="card-header">
                                    <h5 className="mb-0">Lista de Usuarios</h5>
                                </div>
                                <div className="card-body p-0">
                                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                        <ul className="list-group list-group-flush">
                                            {users.map(user => (
                                                <li 
                                                    key={user.id} 
                                                    className={`list-group-item list-group-item-action ${selectedUser && selectedUser.id === user.id ? 'active' : ''}`}
                                                    onClick={() => handleSelectUser(user)}
                                                >
                                                    {user.nombre} <small className="text-muted">({user.rol})</small>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-md-7">
                            <div className="card h-100">
                                <div className="card-header">
                                    <h5 className="mb-0">Crear Nuevo Usuario</h5>
                                </div>
                                <div className="card-body">
                                    <form onSubmit={handleNewUserSubmit}>
                                        <div className="mb-3">
                                            <input type="text" name="nombre" value={newUser.nombre} onChange={handleNewUserChange} className="form-control" placeholder="Nombre completo" required />
                                        </div>
                                        <div className="mb-3">
                                            <input type="email" name="email" value={newUser.email} onChange={handleNewUserChange} className="form-control" placeholder="Correo electrónico" required />
                                        </div>
                                        <div className="mb-3">
                                            <input type="password" name="password" value={newUser.password} onChange={handleNewUserChange} className="form-control" placeholder="Contraseña (mínimo 8 caracteres)" required />
                                        </div>
                                        <div className="mb-3">
                                            <select name="rol" value={newUser.rol} onChange={handleNewUserChange} className="form-select" required>
                                                <option value="usuario">Usuario</option>
                                                <option value="admin">Administrador</option>
                                            </select>
                                        </div>
                                        <button type="submit" className="btn btn-primary w-100">Crear Usuario</button>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {selectedUser && (
                        <div className="mt-4">
                            <div className="card shadow-sm">
                                <div className="card-header">
                                    <h5 className="mb-0">Permisos de {selectedUser.nombre}</h5>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-4">
                                            <h6>Rol Global</h6>
                                            <div className="btn-group w-100">
                                                <button 
                                                    onClick={() => handleRolChange('usuario')} 
                                                    className={`btn btn-sm ${selectedUser.rol === 'usuario' ? 'btn-success' : 'btn-outline-secondary'}`}
                                                >
                                                    Usuario
                                                </button>
                                                <button 
                                                    onClick={() => handleRolChange('admin')} 
                                                    className={`btn btn-sm ${selectedUser.rol === 'admin' ? 'btn-success' : 'btn-outline-secondary'}`}
                                                >
                                                    Admin
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card mt-3 shadow-sm">
                                <div className="card-header d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0">Acceso a Sistemas</h5>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm w-50"
                                        placeholder="Buscar sistema..."
                                        value={filtroSistema}
                                        onChange={(e) => setFiltroSistema(e.target.value)}
                                    />
                                </div>
                                <ul className="list-group list-group-flush" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    {loadingAccesses ? (
                                        <div className="text-center p-3"><div className="spinner-border" role="status"><span className="visually-hidden">Cargando...</span></div></div>
                                    ) : (sistemasFiltrados.map(sistema => {
                                        // ✅ PARTE 2 DE LA CORRECCIÓN: Se asegura que la comparación sea entre números
                                        const tieneAcceso = userAccesses.includes(Number(sistema.id));
                                        return (
                                            <li key={sistema.id} className="list-group-item">
                                                <div className="form-check form-switch">
                                                    <input 
                                                        className="form-check-input" 
                                                        type="checkbox" 
                                                        role="switch" 
                                                        id={`sistema-${sistema.id}`} 
                                                        checked={tieneAcceso}
                                                        onChange={() => tieneAcceso ? handleRevokeSystemPermission(sistema.id) : handleAssignSystemPermission(sistema.id)}
                                                    />
                                                    <label className="form-check-label" htmlFor={`sistema-${sistema.id}`}>{sistema.nombre}</label>
                                                </div>
                                            </li>
                                        )
                                    }))}
                                </ul>
                            </div>

                            <div className="card mt-3 shadow-sm">
                                <div className="card-header d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0">Acceso a Agencias</h5>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm w-50"
                                        placeholder="Buscar agencia..."
                                        value={filtroAgencia}
                                        onChange={(e) => setFiltroAgencia(e.target.value)}
                                    />
                                </div>
                                <ul className="list-group list-group-flush" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    {loadingAccesses ? (
                                        <div className="text-center p-3"><div className="spinner-border" role="status"><span className="visually-hidden">Cargando...</span></div></div>
                                    ) : (agenciasFiltradas.map(agencia => {
                                        // ✅ PARTE 2 DE LA CORRECCIÓN: Se asegura que la comparación sea entre números
                                        const tieneAcceso = userAgencyAccesses.includes(Number(agencia.id));
                                        return (
                                            <li key={agencia.id} className="list-group-item">
                                                <div className="form-check form-switch">
                                                    <input 
                                                        className="form-check-input" 
                                                        type="checkbox" 
                                                        role="switch" 
                                                        id={`agencia-${agencia.id}`} 
                                                        checked={tieneAcceso}
                                                        onChange={() => tieneAcceso ? handleRevokeAgencyPermission(agencia.id) : handleAssignAgencyPermission(agencia.id)}
                                                    />
                                                    <label className="form-check-label" htmlFor={`agencia-${agencia.id}`}>{agencia.nombre}</label>
                                                </div>
                                            </li>
                                        )
                                    }))}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AdminPanel;